import { inject, injectable } from 'inversify';

import { TokenService } from '../http';
import { Logger } from '../logger';

import { ClientMessage, Subscription } from './websocket.types';

const SUCCESS_CLOSE_CODE = 1000;
const ATTEMPTS = 5;
const RECONNECT_BACKOFF_BASE = 2;
const RECONNECT_DELAY = 5000;
const RECONNECT_JITTER = 1000;
const HEARTBEAT_INTERVAL = 30000;
const PONG_TIMEOUT = 10000;
const PING_MESSAGE = JSON.stringify({ type: 'ping' });

interface ListenerHolder<TPayload> {
  bivarianceHack(payload: TPayload): void;
}

type Listener<TPayload> = ListenerHolder<TPayload>['bivarianceHack'];

interface StoredListenerHolder {
  bivarianceHack(payload: unknown): void;
}

type StoredListener = StoredListenerHolder['bivarianceHack'];

type ListenersMap = Map<string, Set<StoredListener>>;

type ConnectionStatusListener = (isConnected: boolean) => void;

@injectable()
export class Socket {
  ws: null | WebSocket;

  private reconnectAttempt: number;

  private listeners: ListenersMap;

  private activeSubscriptions: Subscription[];

  private pendingClientMessages: ClientMessage[];

  private token: TokenService;

  private heartbeatTimer: ReturnType<typeof setInterval> | null;

  private pongTimer: ReturnType<typeof setTimeout> | null;

  private connectionStatusListeners: Set<ConnectionStatusListener>;

  constructor(
    @inject(TokenService) token: TokenService,
    private readonly logger: Logger,
  ) {
    this.listeners = new Map();
    this.activeSubscriptions = [];
    this.pendingClientMessages = [];
    this.token = token;
    this.reconnectAttempt = 0;
    this.heartbeatTimer = null;
    this.pongTimer = null;
    this.connectionStatusListeners = new Set();
  }

  /**
   * Поддерживает ли окружение WebSocket.
   *
   * @returns `true`, если WebSocket доступен
   */
  static get isSupported(): boolean {
    return typeof WebSocket !== 'undefined';
  }

  /**
   * Текущее состояние подключения.
   *
   * @returns `true`, если соединение открыто
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Подписаться на изменения статуса подключения.
   * Колбэк вызывается с актуальным состоянием при каждом open/close.
   *
   * @param callback - Подписчик на изменения статуса
   * @returns Функция отписки
   */
  onConnectionStatus(callback: ConnectionStatusListener): () => void {
    this.connectionStatusListeners.add(callback);

    return () => {
      this.connectionStatusListeners.delete(callback);
    };
  }

  private notifyConnectionStatus(isConnected: boolean): void {
    this.connectionStatusListeners.forEach((listener) => listener(isConnected));
  }

  /** Выполнить подключение к Websocket серверу
   * Осуществляется подписка на события:
   * onopen - срабатывает, когда соединение с Websocket установлено и становится досупен двусторонний обмен сообщениями
   * onmessage - срабатывает каждый раз, когда от Websocket получено сообщение, и происходит рассылка всем подписчикам
   * onerror - срабатывает, когда на Websocket произошла ошибка, логирует сообщение об ошибке будет залогировано
   * onclose - срабатывает, когда Websocket соединение переходит в статус "closed", может выполнить переподключение
   */
  connect() {
    if (!Socket.isSupported) {
      this.notifyConnectionStatus(false);
      return;
    }

    if (this.ws) {
      this.stopHeartbeat();
      this.detachWsHandlers(this.ws);
      this.ws.close();
      this.ws = null;
    }

    this.ws = new WebSocket(
      `${window.REACT_APP_VKHRTEK_WS_API_URL}/ws/connect`,
    );

    this.ws.onopen = () => {
      const token = this.token.get();

      if (this.ws && token) {
        this.ws.send(token);
      }

      if (this.activeSubscriptions.length) {
        this.sendClientMessage({
          type: 'subscribe',
          payload: { subscriptions: this.activeSubscriptions },
        });
      }

      this.flushPendingClientMessages();

      this.reconnectAttempt = 0;
      this.startHeartbeat();
      this.notifyConnectionStatus(true);
    };

    this.ws.onmessage = (message) => {
      const { type, payload } = JSON.parse(message.data) as {
        type: string;
        payload: unknown;
      };

      // Любое входящее сообщение означает, что соединение живо
      this.clearPongTimer();

      if (type === 'pong') {
        return;
      }

      const typeListeners = this.listeners.get(type);

      if (typeListeners) {
        typeListeners.forEach((listener) => listener(payload));
      }
    };

    this.ws.onerror = () => {
      // Событие ошибки WS приходит синхронно внутри стека вызовов React
      // (рендера компонентов). Откладываем логирование в отдельную задачу,
      // чтобы Sentry не прикреплял посторонний call stack React и ошибка WS
      // не влияла на рендер.
      // Нативный WS Event не логируем как есть — Sentry прикрепляет к нему
      // случайный call stack и группирует под сторонним фреймом кода
      setTimeout(() => {
        this.logger.info('WS error', {
          tags: {
            vkdoc_error_type: 'ws',
            http_method: 'WS',
            http_url: `${window.REACT_APP_VKHRTEK_WS_API_URL}/ws/connect`,
            ws_ready_state: this.ws?.readyState ?? null,
            ws_reconnect_attempt: this.reconnectAttempt,
            pathname: window.location.pathname,
          },
        });
      });
    };

    this.ws.onclose = (message) => {
      this.stopHeartbeat();
      this.notifyConnectionStatus(false);

      if (
        message.code !== SUCCESS_CLOSE_CODE &&
        this.reconnectAttempt < ATTEMPTS
      ) {
        // 5с, 10с, 20с, ... + jitter, чтобы тысячи клиентов не переподключались одновременно
        const backoff =
          RECONNECT_BACKOFF_BASE ** this.reconnectAttempt * RECONNECT_DELAY;
        const jitter = Math.random() * RECONNECT_JITTER;

        this.reconnectAttempt++;

        setTimeout(() => {
          this.connect();
        }, backoff + jitter);
      } else {
        this.resetWs();
      }
    };
  }

  /**
   * Добавить подписчика на сообщения определённого типа от Websocket.
   *
   * @param type - Тип сообщения от Websocket
   * @param callback - Подписчик на сообщения от Websocket
   * @returns Функция отписки конкретного listener'а
   */
  on<TPayload = unknown>(
    type: string,
    callback: Listener<TPayload>,
  ): () => void {
    const typeListeners = this.listeners.get(type) ?? new Set<StoredListener>();

    typeListeners.add(callback);
    this.listeners.set(type, typeListeners);

    return () => this.off(type, callback);
  }

  /** Удалить ранее добавленного подписчика. */
  off<TPayload = unknown>(type: string, callback: Listener<TPayload>): void {
    const typeListeners = this.listeners.get(type);

    if (!typeListeners) {
      return;
    }

    typeListeners.delete(callback);
  }

  /** Подписаться на изменения по топику.
   * Если топик объектный, нужно передать objectId.
   * Сообщение отправится сразу при открытом соединении, иначе будет
   * отправлено в onopen.
   */
  subscribe(topic: string, objectId?: string): void {
    const subscription: Subscription = {
      topic,
      ...(objectId ? { object_id: objectId } : {}),
    };

    if (this.hasSubscription(subscription)) {
      return;
    }

    this.activeSubscriptions.push(subscription);

    this.sendClientMessage({
      type: 'subscribe',
      payload: { subscriptions: [subscription] },
    });
  }

  /** Отписаться от топика, симметрично subscribe. */
  unsubscribe(topic: string, objectId?: string): void {
    const target: Subscription = {
      topic,
      ...(objectId ? { object_id: objectId } : {}),
    };

    const index = this.activeSubscriptions.findIndex((subscription) =>
      this.isSameSubscription(subscription, target),
    );

    if (index === -1) {
      return;
    }

    this.activeSubscriptions.splice(index, 1);

    this.sendClientMessage({
      type: 'unsubscribe',
      payload: { subscriptions: [target] },
    });
  }

  /** Закрыть Websocket соединение */
  resetWs(): void {
    this.stopHeartbeat();
    if (this.ws) {
      this.detachWsHandlers(this.ws);
      this.ws.close();
      this.ws = null;
    }
    this.unsubscribeAll();
    this.activeSubscriptions = [];
    this.pendingClientMessages = [];
    this.notifyConnectionStatus(false);
  }

  /** Отвязать обработчики от старого WebSocket-экземпляра */
  private detachWsHandlers(ws: WebSocket): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
  }

  /**
   * Запустить heartbeat: периодически отправлять ping и ждать ответ.
   * Если за PONG_TIMEOUT не пришло ни одного сообщения — соединение считается
   * "мёртвым" (полуоткрытым) и закрывается, что инициирует переподключение
   * через onclose.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) {
        return;
      }

      this.ws.send(PING_MESSAGE);

      this.pongTimer = setTimeout(() => {
        this.ws?.close();
      }, PONG_TIMEOUT);
    }, HEARTBEAT_INTERVAL);
  }

  /** Остановить heartbeat и сбросить таймеры ping/pong */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    this.clearPongTimer();
  }

  /** Сбросить таймер ожидания pong — соединение подтвердило свою активность */
  private clearPongTimer(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private hasSubscription(target: Subscription): boolean {
    return this.activeSubscriptions.some((subscription) =>
      this.isSameSubscription(subscription, target),
    );
  }

  private isSameSubscription(a: Subscription, b: Subscription): boolean {
    return a.topic === b.topic && a.object_id === b.object_id;
  }

  private sendClientMessage(message: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return;
    }

    this.pendingClientMessages.push(message);
  }

  private flushPendingClientMessages(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const pending = this.pendingClientMessages;
    this.pendingClientMessages = [];

    pending.forEach((message) => {
      this.ws?.send(JSON.stringify(message));
    });
  }

  private unsubscribeAll(): void {
    this.listeners.clear();
  }
}
